import { FC, useState } from "react";

import {
    Button,
    Card,
    CardHeader,
    Spinner,
    Toast,
    ToastBody,
    ToastTitle,
    useId,
    useToastController,
} from "@fluentui/react-components";
import { nodeServer } from "../constants";
import { useParams, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import FormInput from "../utility/FormInput";
import bcryptjs from "bcryptjs";

interface MainProps {}

const ResetPassword: FC<MainProps> = () => {
    const { code } = useParams();
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email");
    const [busy, setBusy] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);

    const {
        handleSubmit,
        control,
        watch,
        formState: { errors },
    } = useForm<{ password: string; password_confirmation: string }>({
        mode: "onBlur",
        defaultValues: { password: "", password_confirmation: "" },
    });

    const toasterId = useId("toaster");
    const { dispatchToast } = useToastController(toasterId);
    const notify = (type: "error" | "success", message: string) => {
        if (type === "error") {
            dispatchToast(
                <Toast>
                    <ToastTitle>Error</ToastTitle>
                    <ToastBody>{message}</ToastBody>
                </Toast>,
                { intent: "error" }
            );
        } else {
            dispatchToast(
                <Toast>
                    <ToastTitle>Success</ToastTitle>
                    <ToastBody>{message}</ToastBody>
                </Toast>,
                { intent: "success" }
            );
        }
    };

    const tryResetPassword = async (values: {
        password: string;
        password_confirmation: string;
    }) => {
        setBusy(true);
        const headers = new Headers();
        headers.append("Content-Type", "application/json");

        if (values.password.trim() === "") {
            notify("error", "Password cannot be empty or just blank spaces.");
            setBusy(false);
            return;
        }

        const salt = await bcryptjs.genSalt(10);
        const hash = await bcryptjs.hash(values.password, salt);
        const compare = await bcryptjs.compare(values.password, hash);
        //console.log({ salt: salt, hash: hash, compare: compare });
        if (!compare) {
            console.log("FAILED HASH CREATION - do not create new member");
            notify(
                "error",
                "Hashing and salting password failed.  Your password might be incompatible with standard encryption.  Try a different password.  If the problem persists, contact the webmaster."
            );
            setBusy(false);
            return;
        }

        const result = await fetch(nodeServer + `password/verifycode/${code}`, {
            method: "POST",
            headers: headers,
            body: JSON.stringify({ email: email, hash: hash }),
        });

        if (result.ok) {
            const { success } = await result.json();
            notify("success", success);
            setSuccess(true);
        } else {
            const { error } = await result.json();
            notify("error", error);
        }
        setBusy(false);
    };

    return (
        <>
            <Card>
                {success ? (<><CardHeader header={<h2>Successful Password Reset</h2>}></CardHeader>
                <p>
                    You have successfully changed the password for the account using email: <b>{email}</b>.  You may now login with the new password.
                </p>
                </>
              ) :(<>
                <CardHeader header={<h2>Reset Password</h2>}></CardHeader>
                <p>
                    Enter a new password and repeat in the 2nd blank to confirm
                    you didn't make a typo.
                </p>

                <form onSubmit={handleSubmit(tryResetPassword)} method="POST">
                    <FormInput
                        name="password"
                        type="password"
                        control={control}
                        label="Password"
                        required={true}
                        disabled={busy}
                        autoComplete={"new-password"}
                    />

                    <FormInput
                        name="password_confirmation"
                        type="password"
                        control={control}
                        label="Confirm Password"
                        required={true}
                        disabled={busy}
                        error={errors.password_confirmation}
                        rules={{
                            validate: (val) => {
                                const password = watch("password");
                                return (
                                    password === val ||
                                    "Passwords do NOT match."
                                );
                            },
                        }}
                        autoComplete={"new-password"}
                    />
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            marginTop: 10,
                            gap: 10,
                        }}
                    >
                        <Button
                            appearance="primary"
                            type="submit"
                            disabled={busy}
                        >
                            Submit Password Change
                        </Button>
                        {busy && <Spinner size="small"></Spinner>}
                    </div>
                </form>
                </>)}
            </Card>

            {/* {busy && (
                <div
                    className="test"
                    style={{
                        width: "100vw",
                        height: "100vh",
                        left: 0,
                        top: 0,
                        position: "absolute",
                        background: "#00000055",
                        zIndex: 100000,
                    }}
                >
                    <Spinner
                        style={{
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            position: "absolute",
                        }}
                        size="large"
                    ></Spinner>
                </div>
            )} */}
        </>
    );
};

export default ResetPassword;
